import { estMobileMoney, libelleMoyenEncaissement } from './encaissement'
import { estReglee, tresoreriePourMoyen } from './reglement'
import type { Client, Course, DonneesOps, EcritureManuelle, Facture, Journal, NatureLigne, Organisation } from './types'

/* ====================================================================== */
/*  CE QUE CE MODULE FAIT — ET CE QU'IL REFUSE DE FAIRE                    */
/* ====================================================================== */

/**
 * Générateur d'écritures SYSCOHADA, modèle PRINCIPAL (le CA est tout ce qui
 * est facturé, contrairement au modèle agent de SwiftEats).
 *
 * ⚠️ CE PORTAGE N'EST PAS UNE TRANSPOSITION LITTÉRALE, et c'est délibéré.
 * La version HTML produisait des états FAUX :
 *
 *  ① Elle FABRIQUAIT du chiffre d'affaires : trois mois d'abonnement par
 *    client PME, à partir d'un barème écrit en dur, derrière une condition
 *    sur `billing_mode` — colonne qui n'existe dans aucune migration, donc
 *    toujours vraie.
 *  ② Elle IGNORAIT les vraies factures (`invoices`) : aucune écriture.
 *  ③ Elle lisait des sources que rien ne remplit (`packs`, `subscriptions`,
 *    `saas_setups`) : créances, produits d'avance et provisions toujours nuls.
 *  ④ Ses écritures manuelles vivaient en mémoire : perdues au rechargement.
 *  ⑤ Son chiffre d'affaires « particuliers » ne voyait que les courses des
 *    dernières 24 h (fenêtre du dispatch) — corrigé par `DonneesOps.livrees`.
 *  ⑥ Son résultat additionnait les crédits BRUTS des comptes 7 : une écriture
 *    corrective au débit d'un produit (avoir) ne le diminuait pas.
 *
 * Ici : on n'écrit QUE ce qui repose sur une donnée réelle, et ce qui manque
 * est ANNONCÉ (voir `BLOCS_SANS_SOURCE`) plutôt que simulé.
 *
 * LOT 2 (octobre 2026) — le chiffre d'affaires des clients opérés :
 *  • PME au forfait : une facture MENSUELLE générée par la base (forfait, option, dépassements, suppléments
 *    hors créneau, longue distance) — c'est la facture qui porte produit et créance ;
 *  • particulier : le produit est constaté À LA LIVRAISON, en CRÉANCE (D 411 / C 7061 / C 443) ; la
 *    trésorerie n'apparaît qu'à l'ENCAISSEMENT ENREGISTRÉ (D 551 | 571 | 521 / C 411), plus aucun 551 sans
 *    paiement enregistré ; les frais Wave / Orange Money s'écrivent D 631 / C 551 ;
 *  • une course portée par une facture n'est JAMAIS posée en direct (elle l'était deux fois).
 */

export interface Ecriture {
  date: string
  piece: string
  journal: Journal
  compte: string
  libelle: string
  debit: number
  credit: number
}

/** Ce qui manque, et pourquoi — affiché tel quel dans l'écran Comptabilité. */
export const BLOCS_SANS_SOURCE = [
  {
    titre: 'Packs prépayés',
    detail:
      "Aucune table ne les stocke. Tant qu'elle n'existe pas, ni le produit constaté d'avance (487) ni sa consommation ne peuvent être calculés.",
  },
  {
    titre: 'Prestations PME du mois non encore facturées',
    detail:
      "Le chiffre d'affaires d'une PME au forfait n'entre qu'à la GÉNÉRATION de sa facture mensuelle (onglet Factures), datée du jour de l'émission : aucun produit à recevoir n'est constaté en fin de mois, et le forfait n'est pas étalé en produit constaté d'avance. Une clôture d'exercice doit donc suivre la génération des factures du dernier mois.",
  },
  {
    titre: 'Abonnements SaaS',
    detail:
      'Les abonnements des tenants ne sont pas chargés par cette console. Le compte 7063 reste vide ici — la supervision plateforme les voit en agrégat.',
  },
  {
    titre: 'Prestations one-shot (mise en service)',
    detail: 'Aucune table ne les stocke ; le compte 7068 ne peut pas être alimenté automatiquement (une écriture manuelle le peut).',
  },
] as const

export const LIBELLE_JOURNAL: Record<Journal, string> = {
  VE: 'Ventes',
  AC: 'Achats',
  BQ: 'Banque',
  CA: 'Caisse',
  OD: 'Opérations diverses',
}

/* ====================================================================== */
/*  TVA                                                                    */
/* ====================================================================== */

/** Taux effectif, en fraction. Zéro si l'organisation n'est pas assujettie. */
export function tauxTVA(org: Organisation): number {
  return org.vat_registered ? Number(org.vat_rate || 18) / 100 : 0
}

/**
 * La taxe est **EXTRAITE** du montant facturé, jamais ajoutée : les montants
 * manipulés ici sont ce que le client a réellement payé ou s'est vu annoncer.
 */
export function extraireTVA(ttc: number, taux: number): { ht: number; tva: number } {
  if (taux <= 0 || ttc <= 0) return { ht: ttc, tva: 0 }
  const ht = Math.round(ttc / (1 + taux))
  return { ht, tva: ttc - ht }
}

/**
 * Totaux d'une facture à partir de montants TTC (prix annoncés au client).
 *
 * Chaque ligne porte son HT extrait ; la TVA est la DIFFÉRENCE entre le TTC
 * total et la somme des HT — jamais un recalcul `HT × taux`, qui pouvait faire
 * dériver le TTC facturé d'un franc par rapport au prix annoncé (la version
 * HTML le faisait).
 */
export function calculerFacture(
  lignesTTC: number[],
  taux: number,
): { lignesHT: number[]; ht: number; tva: number; ttc: number } {
  const lignesHT = lignesTTC.map((t) => extraireTVA(Math.max(0, Math.round(t)), taux).ht)
  const ttc = lignesTTC.reduce((s, t) => s + Math.max(0, Math.round(t)), 0)
  const ht = lignesHT.reduce((s, v) => s + v, 0)
  return { lignesHT, ht, tva: ttc - ht, ttc }
}

/* ====================================================================== */
/*  PLAN DE COMPTES SYSCOHADA                                              */
/* ====================================================================== */

export const COMPTE_DEPENSE: Record<string, string> = {
  fuel: '605',
  salary: '6211',
  maintenance: '624',
  other: '658',
}

/**
 * Les catégories que la base ACCEPTE : `public.expense_category` (0001) n'a
 * que ces quatre valeurs, et `record-expense` les vérifie. La version HTML
 * proposait aussi « infra » et « tech » — refusées par la base.
 */
export const CATEGORIES_DEPENSE: readonly { valeur: keyof typeof COMPTE_DEPENSE; libelle: string }[] = [
  { valeur: 'fuel', libelle: 'Carburant (605)' },
  { valeur: 'salary', libelle: 'Rémunération des livreurs (6211)' },
  { valeur: 'maintenance', libelle: 'Entretien et réparations (624)' },
  { valeur: 'other', libelle: 'Charges diverses (658)' },
]

export const CLASSES_SYSCOHADA: Record<number, string> = {
  1: 'Classe 1 — Comptes de ressources durables',
  2: 'Classe 2 — Comptes d’actif immobilisé',
  3: 'Classe 3 — Comptes de stocks',
  4: 'Classe 4 — Comptes de tiers',
  5: 'Classe 5 — Comptes de trésorerie',
  6: 'Classe 6 — Comptes de charges des activités ordinaires',
  7: 'Classe 7 — Comptes de produits des activités ordinaires',
  8: 'Classe 8 — Comptes des autres charges et produits (HAO)',
  9: 'Classe 9 — Comptes des engagements et comptabilité analytique',
}

export interface CompteSyscohada {
  code: string
  label: string
  /** Mouvementé automatiquement par la console. */
  op?: boolean
}

/** Plan SYSCOHADA révisé — repris de la console HTML (libellés officiels). */
export const PLAN_SYSCOHADA: readonly CompteSyscohada[] = [
  { code: '10', label: 'Capital' }, { code: '101', label: 'Capital social' },
  { code: '104', label: 'Primes liées au capital social' }, { code: '105', label: 'Écarts de réévaluation' },
  { code: '106', label: 'Réserves' }, { code: '11', label: 'Report à nouveau' },
  { code: '12', label: 'Résultat net de l’exercice' }, { code: '14', label: 'Subventions d’investissement' },
  { code: '15', label: 'Provisions réglementées et fonds assimilés' }, { code: '16', label: 'Emprunts et dettes assimilées' },
  { code: '162', label: 'Emprunts auprès des établissements de crédit' }, { code: '19', label: 'Provisions pour risques et charges' },
  { code: '20', label: 'Charges immobilisées' }, { code: '21', label: 'Immobilisations incorporelles' },
  { code: '213', label: 'Logiciels et sites internet' }, { code: '22', label: 'Terrains' },
  { code: '23', label: 'Bâtiments, installations et agencements' }, { code: '24', label: 'Matériel, mobilier et actifs biologiques' },
  { code: '2441', label: 'Matériel et mobilier de bureau' }, { code: '2442', label: 'Matériel informatique' },
  { code: '245', label: 'Matériel de transport (flotte)' }, { code: '26', label: 'Titres de participation' },
  { code: '27', label: 'Autres immobilisations financières' }, { code: '28', label: 'Amortissements' },
  { code: '29', label: 'Dépréciations des immobilisations' },
  { code: '31', label: 'Marchandises' }, { code: '32', label: 'Matières premières et fournitures' },
  { code: '33', label: 'Autres approvisionnements' }, { code: '36', label: 'Produits finis' },
  { code: '38', label: 'Stocks en cours de route' }, { code: '39', label: 'Dépréciations des stocks' },
  { code: '40', label: 'Fournisseurs et comptes rattachés' }, { code: '401', label: 'Fournisseurs, dettes en compte' },
  { code: '408', label: 'Fournisseurs, factures non parvenues' }, { code: '41', label: 'Clients et comptes rattachés' },
  { code: '411', label: 'Clients (créances B2B)', op: true }, { code: '416', label: 'Créances clients litigieuses ou douteuses' },
  { code: '42', label: 'Personnel' }, { code: '421', label: 'Personnel, avances et acomptes' },
  { code: '422', label: 'Personnel, rémunérations dues (livreurs)' }, { code: '43', label: 'Organismes sociaux' },
  { code: '44', label: 'État et collectivités publiques' }, { code: '441', label: 'État, impôt sur les bénéfices' },
  { code: '443', label: 'État, TVA facturée', op: true }, { code: '445', label: 'État, TVA récupérable' },
  { code: '447', label: 'État, impôts retenus à la source' }, { code: '46', label: 'Associés et groupe' },
  { code: '47', label: 'Débiteurs et créditeurs divers' }, { code: '487', label: 'Produits constatés d’avance' },
  { code: '49', label: 'Dépréciations et provisions pour risques (tiers)' }, { code: '491', label: 'Provisions créances douteuses', op: true },
  { code: '50', label: 'Titres de placement' }, { code: '52', label: 'Banques' },
  { code: '521', label: 'Banque', op: true }, { code: '5271', label: 'Ancien compte mobile money (avant octobre 2026), à solder vers 551' },
  { code: '53', label: 'Établissements financiers et assimilés' }, { code: '55', label: 'Instruments de monnaie électronique' },
  { code: '551', label: 'Monnaie électronique (Wave / Orange Money)', op: true },
  { code: '57', label: 'Caisse' }, { code: '571', label: 'Caisse' },
  { code: '59', label: 'Dépréciations et provisions pour risques (trésorerie)' },
  { code: '60', label: 'Achats et variations de stocks' }, { code: '605', label: 'Achats (carburant)', op: true },
  { code: '6053', label: 'Fournitures d’électricité, eau, gaz' }, { code: '6055', label: 'Fournitures de bureau' },
  { code: '61', label: 'Transports' }, { code: '62', label: 'Services extérieurs A' },
  { code: '6211', label: 'Rémunération des livreurs', op: true },
  { code: '622', label: 'Locations et charges locatives' }, { code: '624', label: 'Entretien et réparations', op: true },
  { code: '625', label: 'Primes d’assurance' }, { code: '627', label: 'Services extérieurs (API, SMS)' },
  { code: '628', label: 'Frais de télécommunications' }, { code: '63', label: 'Services extérieurs B' },
  { code: '631', label: 'Frais bancaires (mobile money)' }, { code: '637', label: 'Rémunération de personnel extérieur' },
  { code: '64', label: 'Impôts et taxes' }, { code: '65', label: 'Autres charges' },
  { code: '658', label: 'Charges diverses', op: true }, { code: '6581', label: 'Hébergement / infrastructure technique' },
  { code: '6591', label: 'Dotations aux provisions', op: true }, { code: '66', label: 'Charges de personnel' },
  { code: '661', label: 'Rémunérations directes versées au personnel' },
  { code: '664', label: 'Charges sociales' }, { code: '67', label: 'Frais financiers et charges assimilées' },
  { code: '68', label: 'Dotations aux amortissements' }, { code: '69', label: 'Dotations aux provisions et dépréciations' },
  { code: '70', label: 'Ventes' }, { code: '701', label: 'Ventes de marchandises' },
  { code: '706', label: 'Services vendus' }, { code: '7061', label: 'Prestations de livraison (Outsourcing)', op: true },
  { code: '7063', label: 'Abonnements SaaS' }, { code: '7068', label: 'Autres prestations (mise en service, packs expirés)' },
  { code: '707', label: 'Produits accessoires' }, { code: '71', label: 'Subventions d’exploitation' },
  { code: '75', label: 'Autres produits' }, { code: '77', label: 'Revenus financiers et produits assimilés' },
  { code: '78', label: 'Transferts de charges' }, { code: '79', label: 'Reprises de provisions et de dépréciations' },
  { code: '81', label: 'Valeurs comptables des cessions d’immobilisations' }, { code: '82', label: 'Produits des cessions d’immobilisations' },
  { code: '83', label: 'Charges hors activités ordinaires' }, { code: '84', label: 'Produits hors activités ordinaires' },
  { code: '85', label: 'Dotations HAO' }, { code: '86', label: 'Reprises HAO' },
  { code: '87', label: 'Participation des travailleurs' }, { code: '89', label: 'Impôts sur le résultat' },
  { code: '90', label: 'Comptes réfléchis' }, { code: '92', label: 'Comptes de coûts (sections analytiques)' },
  { code: '94', label: 'Comptes de stocks (analytique)' }, { code: '97', label: 'Coûts de revient' },
  { code: '98', label: 'Résultats analytiques' },
]

const LIBELLES_BASE: Record<string, string> = Object.fromEntries(PLAN_SYSCOHADA.map((c) => [c.code, c.label]))

/** Compatibilité : libellés de base, sans les renommages de l'organisation. */
export const LIBELLE_COMPTE: Record<string, string> = LIBELLES_BASE

/** Un code de compte SYSCOHADA : 2 à 8 chiffres, classe 1 à 9. */
export function codeCompteValide(code: string): boolean {
  return /^[1-9]\d{1,7}$/.test(code.trim())
}

export function classeCompte(code: string): number {
  return Number(code[0]) || 0
}

/** Libellé d'un compte : renommage de l'organisation, sinon libellé officiel, sinon le code. */
export function libelleCompte(code: string, plan: Record<string, string> = {}): string {
  return plan[code] ?? LIBELLES_BASE[code] ?? code
}

/** Plan complet = plan officiel + comptes ajoutés par l'organisation. */
export function planComplet(plan: Record<string, string>): CompteSyscohada[] {
  const connus = new Set(PLAN_SYSCOHADA.map((c) => c.code))
  const ajoutes = Object.keys(plan)
    .filter((c) => !connus.has(c) && codeCompteValide(c))
    .map((code) => ({ code, label: plan[code] ?? code }))
  return [...PLAN_SYSCOHADA.map((c) => ({ ...c, label: plan[c.code] ?? c.label })), ...ajoutes].sort((a, b) =>
    a.code.localeCompare(b.code),
  )
}

/* ====================================================================== */
/*  ÉCRITURES MANUELLES                                                    */
/* ====================================================================== */

const JOURNAUX: readonly Journal[] = ['VE', 'AC', 'BQ', 'CA', 'OD']

/**
 * Contrôle d'une écriture manuelle avant enregistrement. Renvoie la liste
 * des motifs de refus (vide = acceptable).
 */
export function controlerEcritureManuelle(
  e: Pick<EcritureManuelle, 'date' | 'debit' | 'credit' | 'montant' | 'libelle' | 'journal'>,
  dateVerrou: string | null = null,
): string[] {
  const motifs: string[] = []
  if (!/^\d{4}-\d{2}-\d{2}$/.test(e.date) || Number.isNaN(new Date(`${e.date}T00:00:00Z`).getTime()))
    motifs.push('Date invalide.')
  if (!codeCompteValide(e.debit)) motifs.push('Compte débité invalide.')
  if (!codeCompteValide(e.credit)) motifs.push('Compte crédité invalide.')
  if (e.debit.trim() === e.credit.trim()) motifs.push('Le compte débité et le compte crédité doivent différer.')
  if (!Number.isInteger(e.montant) || e.montant <= 0) motifs.push('Le montant doit être un nombre entier de francs, supérieur à zéro.')
  if (!e.libelle.trim()) motifs.push('Libellé requis.')
  if (!JOURNAUX.includes(e.journal)) motifs.push('Journal inconnu.')
  if (dateVerrou && e.date <= dateVerrou)
    motifs.push(`La période est clôturée jusqu'au ${dateVerrou} : rouvrez l'exercice ou datez l'écriture après.`)
  return motifs
}

/**
 * Relit le document `ecritures_manuelles` tel que stocké en base.
 *
 * Le document est du jsonb : rien ne garantit sa forme (édition à la main
 * dans Supabase, ancienne version…). Une ligne mal formée est ÉCARTÉE, jamais
 * « réparée » : mieux vaut une écriture absente, visible au contrôle, qu'une
 * écriture inventée qui déséquilibrerait la balance.
 */
export function lireEcrituresManuelles(valeur: unknown): EcritureManuelle[] {
  const brut = (valeur as { ecritures?: unknown } | null)?.ecritures
  if (!Array.isArray(brut)) return []
  const sortie: EcritureManuelle[] = []
  for (const x of brut) {
    if (!x || typeof x !== 'object') continue
    const o = x as Record<string, unknown>
    const e: EcritureManuelle = {
      id: String(o.id ?? ''),
      date: String(o.date ?? ''),
      piece: String(o.piece ?? ''),
      journal: (JOURNAUX as readonly string[]).includes(String(o.journal)) ? (o.journal as Journal) : 'OD',
      libelle: String(o.libelle ?? ''),
      debit: String(o.debit ?? ''),
      credit: String(o.credit ?? ''),
      montant: Number(o.montant),
      cree_le: String(o.cree_le ?? ''),
      cree_par: o.cree_par == null ? null : String(o.cree_par),
    }
    if (!e.id || controlerEcritureManuelle(e).length > 0) continue
    sortie.push(e)
  }
  return sortie
}

/** Numéro de pièce lisible et stable, dérivé de l'identifiant. */
export function pieceManuelle(id: string): string {
  return `MAN-${id.replace(/-/g, '').slice(0, 6).toUpperCase()}`
}

/* ====================================================================== */
/*  GÉNÉRATION DES ÉCRITURES                                               */
/* ====================================================================== */

/**
 * Tous les clients connus, CLOS COMPRIS : une course ancienne ou une facture doit garder le nom et le type de son
 * client dans les livres. (`clients` ne porte que les actifs et en pause, `tousClients` les porte tous ; on
 * fusionne les deux pour qu'aucun appelant ne perde un client clos faute d'avoir rempli le bon champ.)
 */
export function clientsParId(d: Pick<DonneesOps, 'clients'> & Partial<Pick<DonneesOps, 'tousClients'>>): Map<string, Client> {
  const m = new Map<string, Client>()
  for (const c of d.clients) m.set(c.id, c)
  for (const c of d.tousClients ?? []) m.set(c.id, c)
  return m
}

/**
 * Les courses livrées posées EN DIRECT : celles d'un client particulier (ou inconnu : traité comme tel), que
 * AUCUNE facture non annulée ne porte. Source unique de ce que la compta constate à la livraison, des
 * créances de particuliers et de la répartition du 7061.
 */
export function coursesDirectes(
  d: Pick<DonneesOps, 'clients' | 'livrees' | 'billed'> & Partial<Pick<DonneesOps, 'tousClients'>>,
  parId: Map<string, Client> = clientsParId(d),
): Course[] {
  return d.livrees.filter((c) => {
    if (c.statut !== 'livree') return false
    const client = parId.get(c.client_account_id)
    if (client && client.type !== 'particulier') return false
    return !d.billed.has(c.id)
  })
}

/** Ce qu'un paiement réussi a éteint, par course. */
function payeParCourse(encaissements: DonneesOps['encaissements'] | undefined): Map<string, number> {
  const m = new Map<string, number>()
  for (const p of encaissements ?? []) if (p.status === 'succeeded') m.set(p.course_id, (m.get(p.course_id) ?? 0) + p.amount_fcfa)
  return m
}

export function construireEcritures(d: DonneesOps, maintenant: Date = new Date()): Ecriture[] {
  const E: Ecriture[] = []
  const taux = tauxTVA(d.org)

  const poser = (
    date: string,
    piece: string,
    journal: Journal,
    compte: string,
    libelle: string,
    debit: number,
    credit: number,
  ) => {
    /* Une écriture à zéro n'est pas une écriture. */
    if (!debit && !credit) return
    E.push({ date, piece, journal, compte, libelle, debit: debit || 0, credit: credit || 0 })
  }

  const clientParId = clientsParId(d)

  /* ① Courses livrées aux PARTICULIERS — le produit est constaté À LA LIVRAISON, en CRÉANCE :
     D 411 / C 7061 (HT) / C 443 (TVA extraite). La trésorerie n'apparaît PAS ici : elle naît de
     l'encaissement enregistré (②). Les courses des PME ne sont pas posées ici : elles sont facturées, et
     c'est la facture qui porte produit ET créance. Une course d'un particulier portée par une facture
     (`billed`) l'est donc aussi : la poser en direct la compterait DEUX FOIS (elle l'était). */
  const directes = coursesDirectes(d, clientParId)
  for (const c of directes) {
    const nom = clientParId.get(c.client_account_id)?.name ?? 'Client'
    const { ht, tva } = extraireTVA(c.price_fcfa, taux)
    const date = c.delivered_at ?? c.created_at
    poser(date, c.ref, 'VE', '411', `Course ${nom}`, c.price_fcfa, 0)
    poser(date, c.ref, 'VE', '7061', `Course ${nom}`, 0, ht)
    poser(date, c.ref, 'VE', '443', 'TVA collectée', 0, tva)
  }

  /* ② Encaissements ENREGISTRÉS des courses de particuliers (statut `succeeded` ; un paiement annulé
     n'existe plus). Le compte débité suit le moyen : Wave / Orange Money → 551, espèces → caisse 571
     (journal CA), autre → banque 521. Les frais mobile money, figés par la base au taux d'alors, sont une
     charge : D 631 / C 551, DATÉS du paiement. */
  /* Seul un paiement dont la créance est CONSTATÉE ici (course livrée d'un particulier, hors facture) est posé :
     un paiement orphelin — course inconnue, ou déjà portée par une facture — créditerait le 411 sans l'avoir
     débité, ou l'éteindrait deux fois. La base refuse ces cas ; on ne les propage pas aux livres. */
  const courseParId = new Map(directes.map((c) => [c.id, c]))
  for (const p of d.encaissements) {
    if (p.status !== 'succeeded') continue
    const course = courseParId.get(p.course_id)
    if (!course) continue
    const nom = clientParId.get(course.client_account_id)?.name || 'Client'
    const piece = `ENC-${course.ref}`
    const { compte, journal } = tresoreriePourMoyen(p.moyen)
    poser(p.paid_at, piece, journal, compte, `Encaissement ${nom} (${libelleMoyenEncaissement(p.moyen)})`, p.amount_fcfa, 0)
    poser(p.paid_at, piece, journal, '411', `Encaissement ${nom}`, 0, p.amount_fcfa)
    if (estMobileMoney(p.moyen) && p.frais_fcfa > 0) {
      poser(p.paid_at, piece, 'BQ', '631', `Frais ${libelleMoyenEncaissement(p.moyen)} — ${nom}`, p.frais_fcfa, 0)
      poser(p.paid_at, piece, 'BQ', '551', `Frais ${libelleMoyenEncaissement(p.moyen)} — ${nom}`, 0, p.frais_fcfa)
    }
  }

  /* ③ Factures émises. Annulée = rien. L'ÉMISSION (411 / 7061 / 443) est datée
     de l'émission ; le RÈGLEMENT n'existe QUE si la facture est réglée
     (`paid_at` renseigné) et il est daté de `paid_at` — pas de l'émission : une
     facture de décembre payée en janvier éteint sa créance en janvier. Le
     compte débité suit le moyen : espèces → caisse 571 (journal CA), mobile
     money → 551, virement / chèque → banque 521. Un règlement Wave / Orange
     Money porte ses frais (`frais_mm_fcfa`, calculés par la base) : D 631 / C 551. */
  for (const f of d.invoices) {
    if (f.status !== 'issued' && f.status !== 'paid') continue
    const nom = f.client_name || 'Client'
    poser(f.issue_date, f.number, 'VE', '411', `Facture ${nom}`, f.total_ttc_fcfa, 0)
    poser(f.issue_date, f.number, 'VE', '7061', `Prestations ${nom}`, 0, f.subtotal_ht_fcfa)
    poser(f.issue_date, f.number, 'VE', '443', 'TVA collectée', 0, f.vat_fcfa)
    if (estReglee(f) && f.paid_at) {
      const { compte, journal } = tresoreriePourMoyen(f.payment_method)
      poser(f.paid_at, f.number, journal, compte, `Règlement ${nom}`, f.total_ttc_fcfa, 0)
      poser(f.paid_at, f.number, journal, '411', `Règlement ${nom}`, 0, f.total_ttc_fcfa)
      if (estMobileMoney(f.payment_method) && f.frais_mm_fcfa > 0) {
        poser(f.paid_at, f.number, 'BQ', '631', `Frais ${libelleMoyenEncaissement(f.payment_method)} — ${nom}`, f.frais_mm_fcfa, 0)
        poser(f.paid_at, f.number, 'BQ', '551', `Frais ${libelleMoyenEncaissement(f.payment_method)} — ${nom}`, 0, f.frais_mm_fcfa)
      }
    }
  }

  /* ④ Écritures manuelles — persistées dans `org_documents`. */
  for (const m of d.ecrituresManuelles) {
    poser(m.date, m.piece, m.journal, m.debit, m.libelle, m.montant, 0)
    poser(m.date, m.piece, m.journal, m.credit, m.libelle, 0, m.montant)
  }

  /* ⑤ Charges. */
  for (const e of d.expenses) {
    const compte = COMPTE_DEPENSE[e.category] ?? '658'
    const piece = `DEP-${String(e.id).slice(-4).toUpperCase()}`
    poser(e.spent_on, piece, 'AC', compte, e.label, e.amount_fcfa, 0)
    poser(e.spent_on, piece, 'BQ', '521', `Règlement ${e.label}`, 0, e.amount_fcfa)
  }

  /* ⑥ bis Primes « forte demande » payées aux livreurs (0044) : D 6211 « Rémunération des livreurs » (le compte
     du salaire saisi à la main) / C trésorerie selon le moyen — espèces 571 (CA), Wave / Orange Money 551 (BQ),
     virement 521 (BQ). Datée du paiement. ⚠️ Compte à valider par l'expert-comptable : 6211 convient à des
     livreurs prestataires ; des salariés relèveraient de 661 (6612 primes et gratifications). */
  for (const p of d.paiesPrimes ?? []) {
    const piece = `PAIE-${p.mois.slice(0, 7)}-${String(p.id).slice(0, 4).toUpperCase()}`
    const { compte, journal } = tresoreriePourMoyen(p.moyen)
    const libelle = `Primes forte demande ${p.driver_nom} — ${p.mois.slice(0, 7)}`
    poser(p.paye_le, piece, journal, '6211', libelle, p.montant_fcfa, 0)
    poser(p.paye_le, piece, journal, compte, libelle, 0, p.montant_fcfa)
  }

  /* ⑥ Provisions sur créances de plus de 90 jours. */
  const iso = maintenant.toISOString()
  for (const x of creances(d, maintenant).filter((c) => c.age > 90)) {
    poser(iso, `PROV-${x.piece}`, 'OD', '6591', `Dotation provision — ${x.client}`, x.montant, 0)
    poser(iso, `PROV-${x.piece}`, 'OD', '491', `Provision créance douteuse — ${x.client}`, 0, x.montant)
  }

  E.sort(
    (a, b) =>
      new Date(a.date).getTime() - new Date(b.date).getTime() || String(a.piece).localeCompare(String(b.piece)),
  )
  return E
}

/* ====================================================================== */
/*  CRÉANCES                                                               */
/* ====================================================================== */

export interface Creance {
  client: string
  piece: string
  montant: number
  date: string
  age: number
}

/**
 * Ce que les créances, l'ancienneté et les tiers lisent : les factures, et — pour les créances de PARTICULIERS —
 * les courses livrées, leurs paiements et leurs clients. Les champs facultatifs absents donnent « aucune créance
 * de particulier », pas une erreur : un appelant qui ne connaît que les factures reste valide.
 */
export type SourceCreances = Pick<DonneesOps, 'invoices'> &
  Partial<Pick<DonneesOps, 'clients' | 'tousClients' | 'livrees' | 'billed' | 'encaissements'>>

interface CreanceParticulier {
  client: string
  ref: string
  date: string
  prix: number
  paye: number
}

/** Les courses livrées d'un particulier, posées en direct, avec ce qui en a déjà été payé. */
function creancesParticuliers(d: SourceCreances): CreanceParticulier[] {
  if (!d.livrees) return []
  const complet = { clients: d.clients ?? [], tousClients: d.tousClients ?? [], livrees: d.livrees, billed: d.billed ?? new Set<string>() }
  const parId = clientsParId(complet)
  const paye = payeParCourse(d.encaissements)
  return coursesDirectes(complet, parId).map((c) => ({
    client: parId.get(c.client_account_id)?.name ?? 'Client',
    ref: c.ref,
    date: (c.delivered_at ?? c.created_at).slice(0, 10),
    prix: c.price_fcfa,
    paye: paye.get(c.id) ?? 0,
  }))
}

/**
 * Les créances : factures émises et non réglées — tant que `paid_at` est nul,
 * la facture est due (même si son statut dit « payée » : sans date de
 * règlement, aucune écriture n'éteint le 411, et l'auxiliaire doit suivre le
 * compte) — ET les courses livrées de particuliers pas (entièrement) encaissées,
 * pour le RESTE dû : c'est ce que le compte 411 porte depuis le lot 2.
 */
export function creances(d: SourceCreances, maintenant: Date = new Date()): Creance[] {
  const t = maintenant.getTime()
  const age = (iso: string) => Math.max(0, Math.floor((t - new Date(iso).getTime()) / 86_400_000))
  const factures = d.invoices
    .filter((f) => (f.status === 'issued' || f.status === 'paid') && !estReglee(f))
    .map((f) => ({
      client: f.client_name || 'Client',
      piece: f.number,
      montant: f.total_ttc_fcfa,
      date: f.issue_date,
      age: age(f.issue_date),
    }))
  const courses = creancesParticuliers(d)
    .filter((c) => c.prix - c.paye > 0)
    .map((c) => ({ client: c.client, piece: c.ref, montant: c.prix - c.paye, date: c.date, age: age(c.date) }))
  return [...factures, ...courses]
}

export interface Anciennete {
  j0_30: number
  j30_60: number
  j60_90: number
  plus90: number
  total: number
}

export function anciennete(d: SourceCreances, maintenant: Date = new Date()): Anciennete {
  const b: Anciennete = { j0_30: 0, j30_60: 0, j60_90: 0, plus90: 0, total: 0 }
  for (const x of creances(d, maintenant)) {
    b.total += x.montant
    if (x.age > 90) b.plus90 += x.montant
    else if (x.age > 60) b.j60_90 += x.montant
    else if (x.age > 30) b.j30_60 += x.montant
    else b.j0_30 += x.montant
  }
  return b
}

/* ====================================================================== */
/*  TIERS — auxiliaire clients 411                                         */
/* ====================================================================== */

export interface CompteTiers {
  client: string
  facture: number
  regle: number
  ouvert: number
  nbOuvertes: number
  plusAncienne: number
  pieces: Creance[]
}

/**
 * Auxiliaire clients, depuis les VRAIES factures (l'original le tirait
 * d'abonnements et de mises en service que rien ne charge : toujours vide)
 * ET depuis les courses livrées des particuliers (créance constatée à la livraison, éteinte par
 * les encaissements enregistrés) : chaque particulier est son propre tiers.
 */
export function tiers(d: SourceCreances, maintenant: Date = new Date()): CompteTiers[] {
  const m = new Map<string, CompteTiers>()
  const obtenir = (n: string) => {
    let t = m.get(n)
    if (!t) {
      t = { client: n, facture: 0, regle: 0, ouvert: 0, nbOuvertes: 0, plusAncienne: 0, pieces: [] }
      m.set(n, t)
    }
    return t
  }
  for (const f of d.invoices) {
    if (f.status !== 'issued' && f.status !== 'paid') continue
    const t = obtenir(f.client_name || 'Client')
    t.facture += f.total_ttc_fcfa
    if (estReglee(f)) t.regle += f.total_ttc_fcfa
  }
  for (const c of creancesParticuliers(d)) {
    const t = obtenir(c.client)
    t.facture += c.prix
    t.regle += Math.min(c.paye, c.prix)
  }
  for (const x of creances(d, maintenant)) {
    const t = obtenir(x.client)
    t.ouvert += x.montant
    t.nbOuvertes += 1
    t.plusAncienne = Math.max(t.plusAncienne, x.age)
    t.pieces.push(x)
  }
  return [...m.values()].sort((a, b) => b.ouvert - a.ouvert || b.facture - a.facture)
}

/**
 * Rapprochement auxiliaire ↔ compte 411. Un écart signale une écriture
 * manuelle sur le 411 qui ne correspond à aucune facture — à expliquer.
 */
export function rapprochement411(t: CompteTiers[], soldes: LigneBalance[]): { auxiliaire: number; compte: number; ecart: number } {
  const auxiliaire = t.reduce((s, c) => s + c.ouvert, 0)
  const compte = soldes.find((s) => s.code === '411')?.solde ?? 0
  return { auxiliaire, compte, ecart: auxiliaire - compte }
}

/** Courrier de relance prêt à copier — transposé de `opRelance()`. */
export function courrierRelance(t: CompteTiers, expediteur: string, fmt: (n: number) => string, fmtDate: (s: string) => string): string {
  const lignes = [...t.pieces]
    .sort((a, b) => b.age - a.age)
    .map((i) => `  • ${i.piece} du ${fmtDate(i.date)} — ${fmt(i.montant)}  (${i.age} j)`)
    .join('\n')
  return (
    `Objet : Relance — factures en attente de règlement\n\n` +
    `À l'attention de ${t.client},\n\n` +
    `Sauf erreur ou règlement croisé de notre part, les factures suivantes demeurent impayées à ce jour :\n\n` +
    `${lignes}\n\n` +
    `Montant total dû : ${fmt(t.ouvert)}\n\n` +
    `Nous vous remercions de bien vouloir procéder au règlement dans les meilleurs délais. ` +
    `Pour toute difficulté, contactez-nous afin de convenir d'un échéancier.\n\n` +
    `Cordialement,\n${expediteur}`
  )
}

/* ====================================================================== */
/*  ÉTATS                                                                  */
/* ====================================================================== */

export interface LigneBalance {
  code: string
  libelle: string
  debit: number
  credit: number
  solde: number
}

export function balance(ecritures: Ecriture[], plan: Record<string, string> = {}): LigneBalance[] {
  const m = new Map<string, { debit: number; credit: number }>()
  for (const e of ecritures) {
    const a = m.get(e.compte) ?? { debit: 0, credit: 0 }
    a.debit += e.debit
    a.credit += e.credit
    m.set(e.compte, a)
  }
  return [...m.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([code, v]) => ({
      code,
      libelle: libelleCompte(code, plan),
      debit: v.debit,
      credit: v.credit,
      solde: v.debit - v.credit,
    }))
}

export interface Resultat {
  produits: Record<string, number>
  charges: Record<string, number>
  totalProduits: number
  totalCharges: number
  resultat: number
}

/**
 * Compte de résultat, en soldes NETS : produits = crédit − débit des comptes 7,
 * charges = débit − crédit des comptes 6. Un avoir (débit d'un 7) diminue le
 * produit ; l'original l'ignorait.
 */
export function resultat(ecritures: Ecriture[]): Resultat {
  const produits: Record<string, number> = {}
  const charges: Record<string, number> = {}
  for (const e of ecritures) {
    if (e.compte.startsWith('7')) produits[e.compte] = (produits[e.compte] ?? 0) + e.credit - e.debit
    if (e.compte.startsWith('6')) charges[e.compte] = (charges[e.compte] ?? 0) + e.debit - e.credit
  }
  for (const k of Object.keys(produits)) if (produits[k] === 0) delete produits[k]
  for (const k of Object.keys(charges)) if (charges[k] === 0) delete charges[k]
  const totalProduits = Object.values(produits).reduce((s, v) => s + v, 0)
  const totalCharges = Object.values(charges).reduce((s, v) => s + v, 0)
  return { produits, charges, totalProduits, totalCharges, resultat: totalProduits - totalCharges }
}

/** Le contrôle qui compte : total débit = total crédit. */
export function equilibre(ecritures: Ecriture[]): { debit: number; credit: number; ecart: number } {
  const debit = ecritures.reduce((s, e) => s + e.debit, 0)
  const credit = ecritures.reduce((s, e) => s + e.credit, 0)
  return { debit, credit, ecart: debit - credit }
}

/** Rattachement de chaque compte à un P&L : Outsourcing, SaaS ou commun. */
const FLUX: Record<string, 'out' | 'saas' | 'com'> = {
  '7061': 'out', '605': 'out', '624': 'out', '6211': 'out',
  '7063': 'saas', '7068': 'saas', '6581': 'saas', '627': 'saas',
}

export interface ParFlux {
  out: { produits: number; charges: number }
  saas: { produits: number; charges: number }
  com: { produits: number; charges: number }
}

export function resultatParFlux(ecritures: Ecriture[]): ParFlux {
  const S: ParFlux = { out: { produits: 0, charges: 0 }, saas: { produits: 0, charges: 0 }, com: { produits: 0, charges: 0 } }
  for (const e of ecritures) {
    const f = FLUX[e.compte] ?? 'com'
    if (e.compte.startsWith('7')) S[f].produits += e.credit - e.debit
    if (e.compte.startsWith('6')) S[f].charges += e.debit - e.credit
  }
  return S
}

/** CA reconnu par mois (`AAAA-MM`), classe 7 nette. */
export function caMensuel(ecritures: Ecriture[]): { mois: string; ca: number }[] {
  const m = new Map<string, number>()
  for (const e of ecritures) {
    if (!e.compte.startsWith('7')) continue
    const t = new Date(e.date)
    if (Number.isNaN(t.getTime())) continue
    const k = t.toISOString().slice(0, 7)
    m.set(k, (m.get(k) ?? 0) + e.credit - e.debit)
  }
  return [...m.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([mois, ca]) => ({ mois, ca: Math.max(0, ca) }))
}

/** Les cinq grandeurs du bandeau — à ne jamais confondre. */
export function indicateursCompta(soldes: LigneBalance[], r: Resultat) {
  const s = (c: string) => soldes.find((x) => x.code === c)?.solde ?? 0
  return {
    tresorerie: s('521') + s('551') + s('5271') + s('571'),
    caReconnu: -(s('7061') + s('7063') + s('7068')),
    produitsAvance: -s('487'),
    creances: s('411'),
    resultat: r.resultat,
  }
}

/** Écritures groupées par pièce — le vrai journal : débit(s) puis crédit(s). */
export function journalParPiece(ecritures: Ecriture[]): { date: string; piece: string; journal: Journal; lignes: Ecriture[] }[] {
  const groupes: { date: string; piece: string; journal: Journal; lignes: Ecriture[] }[] = []
  const index = new Map<string, number>()
  for (const e of ecritures) {
    const k = `${e.piece}|${e.date}`
    let i = index.get(k)
    if (i === undefined) {
      i = groupes.length
      index.set(k, i)
      groupes.push({ date: e.date, piece: e.piece, journal: e.journal, lignes: [] })
    }
    groupes[i]?.lignes.push(e)
  }
  for (const g of groupes) g.lignes.sort((a, b) => b.debit - b.credit - (a.debit - a.credit))
  return groupes
}

/* ====================================================================== */
/*  RÉPARTITION DU CHIFFRE D'AFFAIRES 7061 PAR NATURE                      */
/* ====================================================================== */

export interface RepartitionCA {
  forfaits: number
  option_longue_distance: number
  depassements: number
  supplements_horaires: number
  longue_distance: number
  /** Majorations « forte demande » des courses incluses au forfait (ligne à part, 0043). Celles des dépassements et
      des particuliers sont DANS le prix de la course (dépassements, courses des particuliers). */
  majorations_tension: number
  /** Lignes de factures saisies à la main (à l'unité) — et tout écart entre le HT d'une facture et ses lignes. */
  courses_unite: number
  /** Courses de particuliers constatées à la livraison. */
  courses_particuliers: number
  total: number
}

/**
 * Le compte 7061 (HT), réparti par nature : forfaits / option longue distance / dépassements / suppléments hors
 * créneau / longue distance / courses facturées à l'unité / courses de particuliers.
 *
 *  • factures émises ou réglées : leurs LIGNES (`kind`, montant HT) ; ce que les lignes ne couvrent pas
 *    (lignes illisibles, facture d'avant la 0032) est rangé en « à l'unité », pour que le total tombe
 *    TOUJOURS sur le 7061 des écritures ;
 *  • particuliers : le HT extrait des courses posées en direct.
 * Une facture annulée n'y figure pas ; les écritures manuelles sur 7061 non plus (elles n'ont pas de nature).
 */
export function repartitionCA(d: DonneesOps): RepartitionCA {
  const r: RepartitionCA = {
    forfaits: 0,
    option_longue_distance: 0,
    depassements: 0,
    supplements_horaires: 0,
    longue_distance: 0,
    majorations_tension: 0,
    courses_unite: 0,
    courses_particuliers: 0,
    total: 0,
  }
  const cle: Record<NatureLigne, keyof Omit<RepartitionCA, 'total'>> = {
    forfait: 'forfaits',
    /* Une course « incluse » vaut 0 F ; si un jour elle valait quelque chose, c'est du forfait. */
    course_incluse: 'forfaits',
    option_longue_distance: 'option_longue_distance',
    depassement: 'depassements',
    supplement_horaire: 'supplements_horaires',
    longue_distance: 'longue_distance',
    majoration_tension: 'majorations_tension',
    course: 'courses_unite',
  }
  const lignes = new Map<string, DonneesOps['lignesFactures']>()
  for (const l of d.lignesFactures ?? []) lignes.set(l.invoice_id, [...(lignes.get(l.invoice_id) ?? []), l])
  for (const f of d.invoices) {
    if (f.status !== 'issued' && f.status !== 'paid') continue
    let couvert = 0
    for (const l of lignes.get(f.id) ?? []) {
      r[cle[l.kind] ?? 'courses_unite'] += l.amount_ht_fcfa
      couvert += l.amount_ht_fcfa
    }
    r.courses_unite += f.subtotal_ht_fcfa - couvert
  }
  const taux = tauxTVA(d.org)
  for (const c of coursesDirectes(d)) r.courses_particuliers += extraireTVA(c.price_fcfa, taux).ht
  r.total =
    r.forfaits + r.option_longue_distance + r.depassements + r.supplements_horaires + r.longue_distance + r.majorations_tension + r.courses_unite + r.courses_particuliers
  return r
}

/** Les libellés de la répartition, dans l'ordre d'affichage. */
export const LIBELLE_REPARTITION: readonly { cle: keyof Omit<RepartitionCA, 'total'>; libelle: string }[] = [
  { cle: 'forfaits', libelle: 'Forfaits mensuels' },
  { cle: 'option_longue_distance', libelle: 'Option longue distance' },
  { cle: 'depassements', libelle: 'Dépassements de forfait' },
  { cle: 'supplements_horaires', libelle: 'Suppléments hors créneau' },
  { cle: 'longue_distance', libelle: 'Longue distance' },
  { cle: 'majorations_tension', libelle: 'Majorations forte demande (courses incluses)' },
  { cle: 'courses_unite', libelle: 'Courses facturées à l’unité' },
  { cle: 'courses_particuliers', libelle: 'Courses des particuliers' },
]

/** Prochain numéro de facture — le serveur fait autorité, ceci n'est qu'un aperçu. */
export function apercuNumeroFacture(org: Organisation, factures: Facture[]): string {
  const annee = new Date().getFullYear()
  const prefixe = org.invoice_prefix || 'FAC'
  const n = factures.filter((f) => f.number?.includes(`-${annee}-`)).length + 1
  return `${prefixe}-${annee}-${String(n).padStart(4, '0')}`
}
